<template>
    <div class="crm-subscription">
        <div class="subscription-layout">
            <!-- LEFT: Current Plan Details -->
            <b-card class="plan-details-card">
                <div class="card-header-row">
                    <span class="card-label">CURRENT PLAN DETAILS</span>
                    <b-badge pill class="badge-active">ACTIVE</b-badge>
                </div>
                <div class="plan-main">
                    <div class="plan-icon-wrap">
                        <i class="fa fa-gift plan-icon"></i>
                    </div>
                    <div class="plan-info">
                        <h2 class="plan-name">{{ plan.name }}</h2>
                        <div class="plan-price">{{ plan.price }} / {{ plan.billing }}</div>
                    </div>
                </div>
                <div class="info-boxes">
                    <div class="info-box">
                        <div class="info-box-label">RENEWAL DATE</div>
                        <div class="info-box-value">{{ plan.renewalDate }}</div>
                    </div>
                    <div class="info-box">
                        <div class="info-box-label">SEATS INCLUDED</div>
                        <div class="info-box-value">{{ plan.seatsIncluded }}</div>
                    </div>
                </div>
                <div class="benefits-section">
                    <div class="benefits-label">PLAN BENEFITS</div>
                    <ul class="benefits-list">
                        <li v-for="(benefit, i) in plan.benefits" :key="i" class="benefit-item">
                            <i class="fa fa-check-circle benefit-icon"></i>
                            <span>{{ benefit }}</span>
                        </li>
                    </ul>
                </div>
            </b-card>

            <!-- RIGHT: Upgrade CTA + Usage Stats -->
            <div class="right-column">
                <b-card class="upgrade-cta-card">
                    <h3 class="upgrade-title">Want to scale this account?</h3>
                    <p class="upgrade-desc">
                        Upgrade to a custom enterprise plan to unlock unlimited scalability and dedicated account management.
                    </p>
                    <b-button variant="light" class="btn-upgrade-now" @click="$emit('upgrade')">UPGRADE PLAN NOW</b-button>
                </b-card>
                <b-card class="usage-card">
                    <div class="card-label mb-3">USAGE STATISTICS</div>
                    <div class="usage-list">
                        <div v-for="item in usageItems" :key="item.key" class="usage-item">
                            <div class="usage-row">
                                <span class="usage-label">{{ item.label }}</span>
                                <span class="usage-value">{{ usageDisplay(item) }}</span>
                            </div>
                            <b-progress class="usage-progress">
                                <b-progress-bar
                                    :value="itemPercent(item)"
                                    :variant="item.barVariant"
                                ></b-progress-bar>
                            </b-progress>
                        </div>
                    </div>
                </b-card>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "CrmSubscription",
    props: {
        accountId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            plan: {
                name: "Enterprise Plan",
                price: "$1,200.00",
                billing: "billed monthly",
                renewalDate: "March 01, 2026",
                seatsIncluded: "20 Active Seats",
                benefits: [
                    "Unlimited Projects",
                    "Priority Support",
                    "Advanced Analytics",
                    "SSO & Advanced Security",
                ],
            },
            usageItems: [
                { key: "api", label: "API REQUESTS", current: 85000, max: 100000, barVariant: "danger" },
                { key: "storage", label: "STORAGE USED", current: 42, max: 50, barVariant: "danger", suffix: "GB" },
                { key: "emails", label: "MARKETING EMAILS", current: 1240, max: 5000, barVariant: "primary" },
            ],
        };
    },
    methods: {
        itemPercent(item) {
            if (item.max <= 0) return 0;
            return Math.round((item.current / item.max) * 100);
        },
        usageDisplay(item) {
            const cur = item.suffix ? `${item.current}${item.suffix}` : this.formatNumber(item.current);
            const max = item.suffix ? `${item.max}${item.suffix}` : this.formatNumber(item.max);
            return `${cur} / ${max}`;
        },
        formatNumber(n) {
            return n >= 1000 ? `${(n / 1000).toFixed(0)},${String(n % 1000).padStart(3, "0")}` : String(n);
        },
    },
};
</script>

<style scoped>
.crm-subscription {
    padding: 0;
}

.subscription-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
}

.plan-details-card {
    border-radius: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    padding: 24px;
}

.plan-details-card >>> .card-body {
    padding: 0;
}

.card-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.card-label {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: #6c757d;
}

.badge-active {
    background-color: #28a745 !important;
    color: #fff !important;
    font-weight: 600;
}

.plan-main {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 24px;
}

.plan-icon-wrap {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #1565c0;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.plan-icon {
    font-size: 1.75rem;
}

.plan-name {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a1d21;
    margin: 0 0 4px 0;
}

.plan-price {
    font-size: 0.95rem;
    color: #6c757d;
}

.info-boxes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 24px;
}

.info-box {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
}

.info-box-label {
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: #6c757d;
    margin-bottom: 4px;
}

.info-box-value {
    font-weight: 700;
    color: #1a1d21;
    font-size: 0.95rem;
}

.benefits-section {
    margin-top: 24px;
}

.benefits-label {
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: #6c757d;
    margin-bottom: 12px;
}

.benefits-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.benefit-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    color: #1a1d21;
}

.benefit-icon {
    color: #28a745;
    font-size: 1rem;
    flex-shrink: 0;
}

.right-column {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.upgrade-cta-card {
    border-radius: 12px;
    border: none;
    background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%);
    box-shadow: 0 2px 8px rgba(21, 101, 192, 0.3);
    padding: 28px;
    text-align: center;
}

.upgrade-cta-card >>> .card-body {
    padding: 0;
}

.upgrade-title {
    font-size: 1.35rem;
    font-weight: 700;
    color: #fff;
    margin: 0 0 12px 0;
}

.upgrade-desc {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.95);
    margin: 0 0 24px 0;
    line-height: 1.5;
}

.btn-upgrade-now {
    border-radius: 8px;
    font-weight: 700;
    color: #1565c0;
    padding: 10px 24px;
}

.usage-card {
    border-radius: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    padding: 24px;
}

.usage-card >>> .card-body {
    padding: 0;
}

.usage-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.usage-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.usage-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.usage-label {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: #495057;
}

.usage-value {
    font-size: 0.9rem;
    font-weight: 600;
    color: #1a1d21;
}

.usage-progress {
    height: 10px;
    border-radius: 10px;
    background-color: #e9ecef;
    overflow: hidden;
}

.usage-progress >>> .progress-bar {
    border-radius: 10px;
}

@media (max-width: 992px) {
    .subscription-layout {
        grid-template-columns: 1fr;
    }
}
</style>